/**
 * The chains this deployment serves, from `/chains.json` next to the bundle:
 * nothing chain-specific is baked into a build. Each chain's public API
 * (`/api/base`, `/api/bsc`) knows nothing of the others, so a view that wants
 * the whole picture asks each one and merges.
 */

import { timing } from "../config";
import { api } from "./api";
import { nameChain } from "./chain";
import { nameExplorers } from "./explorer";
import { getNetworkLabel, getRuntimeConfig, loadRuntimeConfig, normalizeConfig, type RuntimeConfig } from "./runtimeConfig";

export interface ChainEntry {
  key: string;
  /** What the person reads: "Base", "BSC". */
  label: string;
  apiBase: string;
  /** The facts below are absent when the file lacks them or has them malformed. */
  chainId?: number;
  /** Null: the network has no explorer. */
  explorer?: { name: string; url: string } | null;
  walletRpcUrls?: string[];
}

/** The single-chain fallback for a deployment with no chains.json (a local run). */
export function defaultChains(): ChainEntry[] {
  return [{ key: "default", label: getNetworkLabel() ?? "This network", apiBase: "/api" }];
}

const isChain = (value: unknown): value is ChainEntry => {
  const c = value as Partial<ChainEntry> | null;
  return typeof c?.key === "string" && typeof c.label === "string" && typeof c.apiBase === "string";
};

const nonEmpty = (value: unknown): value is string => typeof value === "string" && value !== "";

function isWebUrl(value: unknown): value is string {
  if (typeof value !== "string") return false;
  try {
    const { protocol } = new URL(value);
    return protocol === "https:" || protocol === "http:";
  } catch {
    return false;
  }
}

function readExplorer(value: unknown): { name: string; url: string } | null | undefined {
  if (value === null) return null;
  const e = value as { name?: unknown; url?: unknown } | undefined;
  return nonEmpty(e?.name) && isWebUrl(e.url) ? { name: e.name, url: e.url } : undefined;
}

/** Keeps the fields this app reads, each network fact only when well-formed. */
function readChain(raw: ChainEntry): ChainEntry {
  const { chainId, explorer, walletRpcUrls } = raw as unknown as Record<string, unknown>;
  const id = typeof chainId === "number" && Number.isSafeInteger(chainId) && chainId > 0 ? chainId : undefined;
  const explorerRead = readExplorer(explorer);
  const rpcUrls = Array.isArray(walletRpcUrls) ? walletRpcUrls.filter(isWebUrl) : [];
  return {
    key: raw.key,
    label: raw.label,
    apiBase: raw.apiBase,
    ...(id !== undefined ? { chainId: id } : {}),
    ...(explorerRead !== undefined ? { explorer: explorerRead } : {}),
    ...(rpcUrls.length > 0 ? { walletRpcUrls: rpcUrls } : {}),
  };
}

/** The response body shares the deadline; abort alone cannot bound a stalled or non-cooperating read. */
async function networkRead<T>(read: (signal: AbortSignal) => Promise<T>): Promise<T> {
  const controller = new AbortController();
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<never>((_resolve, reject) => {
    timer = setTimeout(() => {
      controller.abort();
      reject(controller.signal.reason);
    }, timing.wallet.readTimeoutMs);
  });
  try {
    return await Promise.race([read(controller.signal), timeout]);
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Never rejects: an unreachable or malformed file degrades to one chain. Each
 * read also names the explorers it lists, so a link reads as its network's.
 */
export async function loadChains(): Promise<ChainEntry[]> {
  try {
    const parsed: unknown = await networkRead(async (signal) => {
      const response = await fetch("/chains.json", { headers: { accept: "application/json" }, signal });
      return response.ok ? response.json() : null;
    });
    if (!Array.isArray(parsed)) return defaultChains();
    const chains = parsed.filter(isChain).map(readChain);
    nameExplorers(chains);
    return chains.length > 0 ? chains : defaultChains();
  } catch {
    return defaultChains();
  }
}

/**
 * WalletConnect's read endpoints for `chainIds`: each listed network's first
 * wallet RPC, the first network naming a chain id winning. A chain the file
 * gives none gets none, rather than Reown's shared API on the project id.
 */
export function walletRpcMap(chains: readonly ChainEntry[], chainIds: readonly number[]): Record<number, string> {
  const map: Record<number, string> = {};
  for (const { chainId, walletRpcUrls } of chains) {
    const url = walletRpcUrls?.[0];
    if (chainId !== undefined && url !== undefined && chainIds.includes(chainId) && !(chainId in map)) map[chainId] = url;
  }
  return map;
}

/**
 * The chain ids a WalletConnect session is proposed for: the boot descriptor's,
 * then each `/chains.json` network's own descriptor's. They come from the
 * gateways because no session exists yet when the wallet pairs. Never rejects:
 * a network whose descriptor names no chain id is left out.
 */
export async function loadNetworkChainIds(chains?: readonly ChainEntry[]): Promise<number[]> {
  const primary = getRuntimeConfig()?.chainId ?? null;
  const listed = chains ?? (await loadChains());
  const found = await Promise.all(
    listed.map(async (chain) => {
      // `/api` is the boot descriptor's own gateway, already read.
      const chainId =
        chain.apiBase === "/api"
          ? primary
          : await networkRead((signal) => api.chainConfig(chain.apiBase, { signal })).then(
              (raw) => normalizeConfig(raw).chainId,
              () => null,
            );
      if (chainId !== null) nameChain(chainId, chain.label);
      return chainId;
    }),
  );
  const ids: number[] = primary === null ? [] : [primary];
  for (const id of found) if (id !== null && !ids.includes(id)) ids.push(id);
  return ids;
}

/**
 * A network's own public descriptor, for the session's network: its
 * `/chains.json` entry's, else the boot descriptor's. Only one that names the
 * network's chain id counts. Never rejects: null when it cannot be had.
 */
export async function loadNetworkConfig(network: { key: string; chainId: number }): Promise<RuntimeConfig | null> {
  const listed = await loadChains();
  const entry = listed.find((c) => c.key === network.key) ?? null;
  const config =
    entry !== null && entry.apiBase !== "/api"
      ? await networkRead((signal) => api.chainConfig(entry.apiBase, { signal })).then(
          (raw) => normalizeConfig(raw),
          () => null,
        )
      : await networkRead(() => loadRuntimeConfig()).catch(() => null);
  return config !== null && config.chainId === network.chainId ? config : null;
}
